import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Table, 
    Button, 
    Row, 
    Col, 
    Statistic,
    Tag,
    Space,
    Input,
    Select,
    Typography,
    Tooltip,
    message,
    Badge,
    Spin
} from 'antd';
import { 
    DollarOutlined,
    DownloadOutlined,
    SearchOutlined,
    TrophyOutlined,
    CalendarOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined,
    ExclamationCircleOutlined,
    TeamOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { CompanyPaymentRecord, PaymentStats, PaymentFilters } from '../../../types/company.types';
import companyService from '../../../services/company/company.service';
import dayjs from 'dayjs';
import { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { Title, Text } = Typography;

const PaymentReportsPage: React.FC = () => {
    const [payments, setPayments] = useState<CompanyPaymentRecord[]>([]);
    const [stats, setStats] = useState<PaymentStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState<PaymentFilters>({});
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchPaymentData();
    }, [filters]);

    // Şirket verilerinden payment bilgilerini çıkar
    const extractPaymentData = (companies: any[]): CompanyPaymentRecord[] => {
        const allPayments: CompanyPaymentRecord[] = [];
        
        companies.forEach(company => {
            if (company?.paymentRecords) {
                company?.paymentRecords.forEach((payment: CompanyPaymentRecord) => {
                    allPayments.push({
                        ...payment,
                        companyId: company.id,
                        companyName: company.name
                    });
                });
            }
        });
        
        return allPayments;
    };

    // Payment istatistiklerini hesapla
    const calculatePaymentStats = (payments: CompanyPaymentRecord[]): PaymentStats => {
        const totalRevenue = payments.reduce((sum, payment) => 
            payment.paymentStatus === 'paid' ? sum + payment.amount : sum, 0);
        
        const currentMonth = dayjs().format('YYYY-MM');
        const monthlyRevenue = payments
            .filter(payment => payment.paymentStatus === 'paid' && dayjs(payment.paymentDate).format('YYYY-MM') === currentMonth)
            .reduce((sum, payment) => sum + payment.amount, 0);

        const totalTransactions = payments.length;
        const activeSubscriptions = payments.filter(payment => 
            payment.paymentStatus === 'paid' && dayjs(payment.expiryDate).isAfter(dayjs())
        ).length;

        const averageOrderValue = totalTransactions > 0 ? Math.round(totalRevenue / totalTransactions) : 0;

        // Son 6 aylık gelir verisi
        const monthlyRevenueData = [];
        for (let i = 5; i >= 0; i--) {
            const month = dayjs().subtract(i, 'months');
            const monthKey = month.format('YYYY-MM');
            const monthName = month.format('MMM');
            
            const monthPayments = payments.filter(payment => 
                payment.paymentStatus === 'paid' && dayjs(payment.paymentDate).format('YYYY-MM') === monthKey
            );
            
            monthlyRevenueData.push({
                month: monthName,
                revenue: monthPayments.reduce((sum, payment) => sum + payment.amount, 0),
                transactions: monthPayments.length
            });
        }

        // Durum dağılımı
        const paymentStatusDistribution = {
            paid: payments.filter(p => p.paymentStatus === 'paid').length,
            pending: payments.filter(p => p.paymentStatus === 'pending').length,
            failed: payments.filter(p => p.paymentStatus === 'failed').length,
            refunded: payments.filter(p => p.paymentStatus === 'refunded').length,
        };

        // Plan tipi dağılımı
        const planTypeDistribution = {
            premium: payments.filter(p => p.planType === 'premium' && p.paymentStatus === 'paid').length,
            basic: payments.filter(p => p.planType === 'basic' && p.paymentStatus === 'paid').length,
            enterprise: payments.filter(p => p.planType === 'enterprise' && p.paymentStatus === 'paid').length,
        };

        return {
            totalRevenue,
            monthlyRevenue,
            totalTransactions,
            activeSubscriptions,
            trialUsers: 0,
            canceledSubscriptions: 0,
            averageRevenuePerUser: 0,
            averageOrderValue,
            revenueGrowth: 0,
            topPayingCompanies: [],
            monthlyRevenueData,
            paymentStatusDistribution,
            planTypeDistribution
        };
    };

    // Filtreleri uygula
    const applyFilters = (payments: CompanyPaymentRecord[], filters: PaymentFilters): CompanyPaymentRecord[] => {
        return payments.filter(payment => {
            if (filters.paymentStatus && payment.paymentStatus !== filters.paymentStatus) {
                return false;
            }
            if (filters.planType && payment.planType !== filters.planType) {
                return false;
            }
            if (filters.dateRange && filters.dateRange.length === 2) {
                const paymentDate = dayjs(payment.paymentDate);
                const startDate = dayjs(filters.dateRange[0]);
                const endDate = dayjs(filters.dateRange[1]);
                if (!paymentDate.isBetween(startDate, endDate, 'day', '[]')) {
                    return false;
                }
            }
            return true;
        });
    };

    const fetchPaymentData = async () => {
        try {
            setLoading(true);
            
            // Şirket verilerini getir
            const companies = await companyService.getAllCompanies();
            
            // Şirket verilerinden payment bilgilerini çıkar
            const allPayments = extractPaymentData(companies);
            const statsData = calculatePaymentStats(allPayments);
            
            // Filtreleri uygula
            const filteredPayments = applyFilters(allPayments, filters);
            
            setPayments(filteredPayments);
            setStats(statsData);
        } catch (error) {
            message.error('Ödeme verileri yüklenirken hata oluştu');
            console.error('Payment data fetch error:', error);
            // Hata durumunda boş state'ler bırak
            setPayments([]);
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number, currency: string = 'USD') => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'paid': return 'success';
            case 'pending': return 'processing';
            case 'failed': return 'error';
            case 'refunded': return 'warning';
            default: return 'default';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'paid': return 'Ödendi';
            case 'pending': return 'Bekliyor';
            case 'failed': return 'Başarısız';
            case 'refunded': return 'İade Edildi';
            default: return status;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'paid': return <CheckCircleOutlined />;
            case 'pending': return <ClockCircleOutlined />;
            case 'failed': return <CloseCircleOutlined />;
            case 'refunded': return <SyncOutlined />;
            default: return <ExclamationCircleOutlined />;
        }
    };

    const getPlanColor = (type: string) => {
        switch (type?.toLowerCase()) {
            case 'monthly': return 'gold';
            case 'annually': return 'purple';
            case 'trial': return 'blue';
            default: return 'default';
        }
    };

    const columns: ColumnsType<CompanyPaymentRecord> = [
        {
            title: 'İşlem ID',
            dataIndex: 'id',
            key: 'id',
            width: 150,
            render: (id: string) => (
                <Text code className="small">
                    {id.slice(-8)}
                </Text>
            ),
        },
        {
            title: 'Şirket',
            dataIndex: 'companyName',
            key: 'companyName',
            render: (name: string) => (
                <div>
                    <Text strong>{name}</Text>
                </div>
            ),
        },
        {
            title: 'Plan',
            dataIndex: 'planType',
            key: 'planType',
            render: (planType: string, record: CompanyPaymentRecord) => (
                <div>
                    <Tag color={getPlanColor(planType)} className="mb-1">
                        {planType?.toUpperCase()}
                    </Tag>
                    <div className="small text-muted">
                        {record.userLimit} kullanıcı
                    </div>
                </div>
            ),
        },
        {
            title: 'Tutar',
            dataIndex: 'amount',
            key: 'amount',
            sorter: (a, b) => a.amount - b.amount,
            render: (amount: number, record: CompanyPaymentRecord) => (
                <div className="text-end">
                    <Text strong className="text-success">
                        {formatCurrency(amount, record.currency)}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Durum',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            filters: [
                { text: 'Ödendi', value: 'paid' },
                { text: 'Bekliyor', value: 'pending' },
                { text: 'Başarısız', value: 'failed' },
                { text: 'İade Edildi', value: 'refunded' },
            ],
            onFilter: (value, record) => record.paymentStatus === value,
            render: (status: string) => (
                <Badge 
                    status={getStatusColor(status) as any}
                    text={
                        <span>
                            {getStatusIcon(status)} {getStatusText(status)}
                        </span>
                    }
                />
            ),
        },
        {
            title: 'Ödeme Tarihi',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            sorter: (a, b) => dayjs(a.paymentDate).unix() - dayjs(b.paymentDate).unix(),
            render: (date: string) => (
                <div>
                    <div>{dayjs(date).format('DD.MM.YYYY')}</div>
                    <Text type="secondary" className="small">
                        {dayjs(date).format('HH:mm')}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Süre',
            key: 'duration',
            render: (record: CompanyPaymentRecord) => (
                <div>
                    <div className="small">
                        <CalendarOutlined className="me-1" />
                        {dayjs(record.paymentDate).format('DD.MM.YYYY')}
                    </div>
                    <div className="small text-muted">
                        {dayjs(record.expiryDate).format('DD.MM.YYYY')}
                    </div>
                </div>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 100,
            render: (record: CompanyPaymentRecord) => (
                <Space>
                    <Tooltip title="Detayları Görüntüle">
                        <Button 
                            type="text" 
                            icon={<EyeOutlined />} 
                            size="small"
                            onClick={() => message.info('Detay sayfası geliştiriliyor')}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const filteredPayments = payments.filter(payment => 
        payment.companyName?.toLowerCase().includes(searchText?.toLowerCase()) ||
        payment.id?.toLowerCase().includes(searchText?.toLowerCase()) ||
        payment.description?.toLowerCase().includes(searchText?.toLowerCase())
    );

    const handleExport = () => {
        // Excel/CSV export işlemi
        // console.log('Export payments data');
    };

    if (loading && payments.length === 0) {
        return (
            <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
                <div className="content flex-fill h-100">
                    <div className="p-6 text-center">
                        <Spin size="large" />
                        <div className="mt-3">
                            <Text>Ödeme verileri yükleniyor...</Text>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
            <div className="content flex-fill h-100">
                <div className="p-6">
                    <div className="mb-6">
                        <Title level={3}>Ödeme Raporları</Title>
                        <Text type="secondary">
                            Tüm şirketlerin ödeme geçmişi ve abonelik durumları
                        </Text>
                    </div>

                    {/* İstatistikler */}
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Toplam Gelir"
                                    value={stats?.totalRevenue}
                                    precision={2}
                                    prefix={<DollarOutlined />}
                                    suffix="₺"
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Aylık Gelir"
                                    value={stats?.monthlyRevenue}
                                    precision={2}
                                    prefix={<CalendarOutlined />}
                                    suffix="₺"
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Aktif Abonelik"
                                    value={stats?.activeSubscriptions}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Deneme Kullanıcıları"
                                    value={stats?.trialUsers}
                                    prefix={<TeamOutlined />}
                                    valueStyle={{ color: '#faad14' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="İptal Edilen"
                                    value={stats?.canceledSubscriptions}
                                    prefix={<CloseCircleOutlined />}
                                    valueStyle={{ color: '#ff4d4f' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={6} lg={4}>
                            <Card>
                                <Statistic
                                    title="Kullanıcı Başına Gelir"
                                    value={stats?.averageRevenuePerUser}
                                    precision={2}
                                    prefix={<TrophyOutlined />}
                                    suffix="₺"
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtreler */}
                    <Card className="mb-4">
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={12} md={6}>
                                <Input
                                    placeholder="Şirket adı ara..."
                                    prefix={<SearchOutlined />}
                                    value={filters.companyName}
                                    onChange={(e) => setFilters({ ...filters, companyName: e.target.value })}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Ödeme durumu"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={filters.paymentStatus}
                                    onChange={(value) => setFilters({ ...filters, paymentStatus: value })}
                                >
                                    <Option value="paid">Ödendi</Option>
                                    <Option value="pending">Bekliyor</Option>
                                    <Option value="failed">Başarısız</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Select
                                    placeholder="Plan tipi"
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={filters.planType}
                                    onChange={(value) => setFilters({ ...filters, planType: value })}
                                >
                                    <Option value="trial">Deneme</Option>
                                    <Option value="premium">Premium</Option>
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<ReloadOutlined />}
                                        onClick={fetchPaymentData}
                                        loading={loading}
                                    >
                                        Yenile
                                    </Button>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        onClick={handleExport}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    {/* Ödeme Tablosu */}
                    <Card>
                        <Table
                            columns={columns}
                            dataSource={filteredPayments}
                            rowKey="id"
                            loading={loading}
                            pagination={{
                                pageSize: 20,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} / ${total} ödeme`,
                            }}
                            scroll={{ x: 1000 }}
                            size="small"
                        />
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default PaymentReportsPage; 